// The Auto Breeze controls on the call screen. When the owner joins the room
// an Auto Breeze phone call is bridged into, this shows who the call is with,
// where it stands, the live transcript, and Take over / Hand back / Hang up
// (QUEUE row 35). The room is found through GET /api/cadence/rooms/:code,
// which answers only for the call's owner, so a guest never sees any of it.

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { CADENCE_FINAL, type CadenceCall, type CadenceLine, type CadenceLiveMessage } from "@breeze/core";
import { api, ApiError } from "../lib/api";
import { getUserKey } from "../lib/storage";

function statusLine(call: CadenceCall): string {
  switch (call.status) {
    case "dialing":
      return "Calling";
    case "ringing":
      return "Ringing";
    case "on_hold":
      return "On hold";
    case "talking":
      return call.mode === "adam" ? "You're on the line" : "The AI is talking";
    case "needs_you":
      return call.needsReason ? `Needs you: ${call.needsReason}` : "Needs you";
    case "with_you":
      return call.mode === "adam" ? "You're on the line" : "The AI is talking";
    case "done":
      return call.outcome ?? "Call ended";
    case "no_answer":
      return "No answer";
    case "busy":
      return "Busy";
    case "failed":
      return "Could not connect";
    case "canceled":
      return "Hung up";
    default:
      return "";
  }
}

const SPEAKER: Record<CadenceLine["speaker"], string> = { them: "Them", breeze: "AI", adam: "You", note: "" };

export function CadencePanel({ slug }: { readonly slug: string }): JSX.Element | null {
  const [call, setCall] = useState<CadenceCall | null>(null);
  const [lines, setLines] = useState<readonly CadenceLine[]>([]);
  const [interim, setInterim] = useState<{ speaker: "them" | "adam"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Which call this room belongs to. A 404 means it is an ordinary call.
  useEffect(() => {
    if (!getUserKey()) return;
    const controller = new AbortController();
    api<CadenceCall>(`/api/cadence/rooms/${encodeURIComponent(slug)}`, { signal: controller.signal })
      .then((c) => {
        setCall(c);
        setLines(c.transcript);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [slug]);

  const callId = call?.id ?? null;
  const ended = call ? CADENCE_FINAL.includes(call.status) : false;

  // The live feed, reconnecting while the call runs.
  useEffect(() => {
    const key = getUserKey();
    if (!callId || !key || ended) return;
    let socket: WebSocket | null = null;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const connect = async (): Promise<void> => {
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      // A minute-long ticket for this feed, not the user key, in the URL.
      let ticket: string;
      try {
        ticket = (await api<{ ticket: string }>(`/api/cadence/calls/${callId}/ticket`, { method: "POST", body: {} })).ticket;
      } catch {
        if (!stopped) retry = setTimeout(() => void connect(), 3000);
        return;
      }
      if (stopped) return;
      socket = new WebSocket(`${proto}://${window.location.host}/api/cadence/calls/${callId}/live?ticket=${encodeURIComponent(ticket)}`);
      socket.onmessage = (event) => {
        let message: CadenceLiveMessage;
        try {
          message = JSON.parse(String(event.data)) as CadenceLiveMessage;
        } catch {
          return;
        }
        if (message.type === "call") {
          setCall(message.call);
          setLines(message.call.transcript);
        } else if (message.type === "line") {
          setLines((cur) => [...cur, message.line]);
          setInterim((cur) => (cur && cur.speaker === message.line.speaker ? null : cur));
        } else if (message.type === "interim") {
          setInterim(message.text ? { speaker: message.speaker, text: message.text } : null);
        }
      };
      socket.onclose = () => {
        if (!stopped) retry = setTimeout(() => void connect(), 1500);
      };
    };
    void connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      socket?.close();
    };
  }, [callId, ended]);

  // Keep the newest line in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, interim, open]);

  const act = useCallback(
    async (path: string, body?: unknown): Promise<void> => {
      if (!callId) return;
      setBusy(true);
      setError(null);
      try {
        const next = await api<CadenceCall | { call?: CadenceCall }>(`/api/cadence/calls/${callId}/${path}`, { method: "POST", body: body ?? {} });
        const updated = (next as { call?: CadenceCall }).call ?? (next as CadenceCall);
        if (updated && typeof updated === "object" && "status" in updated) setCall(updated);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "That did not go through. Try again.");
      } finally {
        setBusy(false);
      }
    },
    [callId],
  );

  if (!call) return null;
  const name = call.toName ?? call.to;
  const speaking = call.mode === "adam";
  const shown = lines.filter((l) => l.speaker !== "note" || l.text).slice(-40);

  return (
    <div
      className="cadence-panel"
      role="region"
      aria-label={`Phone call with ${name}`}
      style={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
        width: "min(480px, calc(100% - 2 * var(--space-4)))",
        borderRadius: 16,
        background: "rgba(22,25,30,0.94)",
        color: "#fff",
        zIndex: 28,
        boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
        animation: "breeze-fade 0.16s var(--ease)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          width: "100%",
          alignItems: "baseline",
          gap: 8,
          padding: "12px 16px",
          border: "none",
          background: "transparent",
          color: "inherit",
          textAlign: "left",
          cursor: "pointer",
          minHeight: 44,
        }}
      >
        <span style={{ fontWeight: 650, fontSize: 15, flexShrink: 0 }}>{name}</span>
        <span
          style={{
            fontSize: 14,
            color: call.status === "needs_you" ? "#ffcc80" : "#aeb4bc",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {statusLine(call)}
        </span>
      </button>

      {open ? (
        <div
          ref={listRef}
          style={{ maxHeight: 148, overflowY: "auto", padding: "0 16px 4px", fontSize: 14, lineHeight: 1.4 }}
        >
          {shown.length === 0 && !interim ? <p style={{ margin: 0, color: "#8b929a" }}>Nothing said yet.</p> : null}
          {shown.map((line, i) =>
            line.speaker === "note" ? (
              <p key={i} style={{ margin: "2px 0", color: "#8b929a", fontSize: 13 }}>
                {line.text}
              </p>
            ) : (
              <p key={i} style={{ margin: "2px 0" }}>
                <span style={{ color: line.speaker === "them" ? "#9ecbff" : "#aeb4bc", fontWeight: 600 }}>{SPEAKER[line.speaker]}</span>{" "}
                {line.tone ? <span style={{ color: "#8b929a" }}>({line.tone}) </span> : null}
                {line.text}
              </p>
            ),
          )}
          {interim ? (
            <p style={{ margin: "2px 0", color: "#8b929a" }}>
              <span style={{ fontWeight: 600 }}>{SPEAKER[interim.speaker]}</span> {interim.text}
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? <p style={{ margin: "4px 16px 0", color: "#ff8a80", fontSize: 13 }}>{error}</p> : null}

      {ended ? null : (
        <div style={{ display: "flex", gap: 8, padding: "10px 12px 12px" }}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => void act("mode", { mode: speaking ? "ai" : "adam" })}
            style={{ flex: 1, minHeight: 44 }}
          >
            {speaking ? "Hand back" : "Take over"}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy}
            onClick={() => void act("cancel")}
            style={{ minHeight: 44, padding: "0 16px" }}
          >
            Hang up
          </button>
        </div>
      )}
    </div>
  );
}
